import type { HostApi, PluginModule } from '@easydb/shared';
import { SAFE_MODE } from './safe-mode.js';
import { builtinKey } from './builtin-key.js';
import * as csvImport from '../plugins/csv-import.js';
import * as csvExport from '../plugins/csv-export.js';
import * as jsonExport from '../plugins/json-export.js';
import * as jsonImport from '../plugins/json-import.js';
import * as sqlImport from '../plugins/sql-import.js';
import * as connectMenu from '../plugins/connect-menu.js';
import * as datasetteImport from '../plugins/datasette-import.js';
import * as datasetteConnect from '../plugins/datasette-connect.js';
import * as urlSource from '../plugins/url-source.js';
import * as dumpExport from '../plugins/dump-export.js';
import * as sqlExport from '../plugins/sql-export.js';
import * as gistSync from '../plugins/gist-sync.js';
import * as newTableButton from '../plugins/new-table-button.js';
import * as cellDate from '../plugins/cell-date.js';
import * as cellDatetime from '../plugins/cell-datetime.js';
import * as dateFilter from '../plugins/date-filter.js';
import * as cellBoolean from '../plugins/cell-boolean.js';
import * as cellColor from '../plugins/cell-color.js';
import * as cellImage from '../plugins/cell-image.js';
import * as preview from '../plugins/preview.js';
import * as htmlRender from '../plugins/html-render.js';
import * as cellMarkdown from '../plugins/cell-markdown.js';
import * as cellLink from '../plugins/cell-link.js';
import * as cellTags from '../plugins/cell-tags.js';
import * as autoRenderer from '../plugins/auto-renderer.js';
import * as deleteTable from '../plugins/delete-table.js';
import * as validate from '../plugins/validate.js';
import * as runScripts from '../plugins/run-scripts.js';
import * as tableCopy from '../plugins/table-copy.js';
import * as sqlConsole from '../plugins/sql-console.js';
import * as importData from '../plugins/import-data.js';
import * as views from '../plugins/views.js';
import * as settings from '../plugins/settings.js';
import * as projection from '../plugins/projection.js';
import * as vizCharts from '../plugins/viz-charts.js';
import * as vizMap from '../plugins/viz-map.js';
import * as vizWordcloud from '../plugins/viz-wordcloud.js';
import * as vizCustom from '../plugins/viz-custom.js';
import * as vizRecord from '../plugins/viz-record.js';
import * as commandPaletteButton from '../plugins/command-palette-button.js';
import * as electronDb from '../plugins/electron-db.js';
import * as sqliteFileSource from '../plugins/sqlitefile-source.js';
import * as tips from '../plugins/tips.js';
import * as edbFile from '../plugins/edb-file.js';
import * as legacyImport from '../plugins/legacy-import.js';
import * as commandlets from '../plugins/commandlets.js';
import * as editRecord from '../plugins/edit-record.js';
import * as newPlugins from '../plugins/new-plugins.js';

/** A built-in plugin paired with its id (mirrors `meta.id`, cheaply reachable without importing every module). */
export interface BuiltinEntry {
  id: string;
  meta: NonNullable<PluginModule['meta']>;
  module: PluginModule;
}

/**
 * Built-in plugins shipped with the renderer. They satisfy the same
 * PluginModule contract as URL-loaded plugins; the only difference is the
 * delivery mechanism (static import vs. dynamic import of a Blob URL).
 *
 * Only plugins flagged `meta.fixed = true` are always-on and non-disableable
 * (currently only `settings`). Every other built-in is user-toggleable from
 * the Plugin Manager and defaults to enabled. Disabled state is stored in the
 * plugins collection under the synthetic key `builtin:<id>`.
 *
 * The Plugin Manager itself is opened from a core header button (see
 * `app-shell.ts`) — it is not a plugin.
 */
const modules: PluginModule[] = [
  settings,
  newTableButton,
  csvImport,
  jsonImport,
  sqlImport,
  connectMenu,
  datasetteImport,
  datasetteConnect,
  urlSource,
  csvExport,
  jsonExport,
  dumpExport,
  sqlExport,
  gistSync,
  cellDate,
  cellDatetime,
  dateFilter,
  cellBoolean,
  cellColor,
  cellImage,
  preview,
  htmlRender,
  cellMarkdown,
  cellLink,
  cellTags,
  autoRenderer,
  deleteTable,
  validate,
  runScripts,
  tableCopy,
  sqlConsole,
  importData,
  views,
  projection,
  vizCharts,
  vizMap,
  vizWordcloud,
  vizCustom,
  vizRecord,
  commandPaletteButton,
  electronDb,
  sqliteFileSource,
  tips,
  edbFile,
  legacyImport,
  commandlets,
  editRecord,
  // Last on purpose: `load()` runs these in order and awaits each one, and this is
  // the only entry that both fetches from the network and can open a dialog. Going
  // last keeps its question off the top of `tips`'s and `legacy-import`'s, and its
  // fetch out of everyone else's way.
  newPlugins,
];

function requireMeta(p: PluginModule): NonNullable<PluginModule['meta']> {
  if (!p.meta) throw new Error('Built-in plugin is missing meta');
  return p.meta;
}

const builtins: BuiltinEntry[] = modules.map((module) => {
  const meta = requireMeta(module);
  return { id: meta.id, meta, module };
});

/** Public for the Plugin Manager dialog so it can render icon/name/id/author/repo. */
export const builtinPlugins = builtins;

/** Synthetic URL used to key built-in disable state in the plugins collection. */
export { builtinKey } from './builtin-key.js';

/**
 * Runs init() on every built-in plugin (skipping non-fixed ones the user
 * disabled). Returns a function that runs load() on the same set once the
 * app shell is ready.
 */
export async function loadBuiltinPlugins(api: HostApi): Promise<() => Promise<void>> {
  const active: BuiltinEntry[] = [];
  for (const entry of builtins) {
    if (await isDisabled(api, entry)) continue;
    active.push(entry);
    try {
      await entry.module.init?.(api);
    } catch (err) {
      api.events.emit('plugin:error', {
        url: entry.id,
        phase: 'init',
        error: err,
      });
    }
  }

  return async () => {
    for (const entry of active) {
      try {
        await entry.module.load?.(api);
      } catch (err) {
        api.events.emit('plugin:error', {
          url: entry.id,
          phase: 'load',
          error: err,
        });
      }
    }
  };
}

async function isDisabled(api: HostApi, entry: BuiltinEntry): Promise<boolean> {
  if (entry.meta.fixed) return false;
  // ?safemode ('all-optional'): skip every non-fixed built-in for this boot
  // only — never persisted, never touches the plugins collection. See
  // safe-mode.ts for why this must stay transient.
  if (SAFE_MODE === 'all-optional') return true;
  const rec = await api.store.plugins.findOne(builtinKey(entry.id));
  return rec?.enabled === false;
}
